import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import Card from "../../../components/card";
import TemplateDefault from "../../../components/template-default";
import InputDateTime from "../../../components/input-datetime";
import SelectComponent from "../../../components/select-component";
import useFetchData from "../hooks/use-fetch-data";
import InputForm from "../../../components/input-form";
import Button from "../../../components/button";
import {useNavigate, useParams} from "react-router-dom";
import general from "../../../utils/general";
import requestService from "../../../services/request-service";
import {toast} from "react-toastify";
import {AlertMessage} from "../../../utils/alert-message";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import MySwal from "sweetalert2";
import {FaExternalLinkAlt, FaPlus} from "react-icons/fa";
import AttachesTable from "../../../components/attaches-table";
import InputCheckbox from "../../../components/input-checkbox";
import integrationsService from "../../../services/integrations-service";
import IntegrationsService from "../../../services/integrations-service";
import {ProjectContext} from "../../../context/project-context";
import {EditorContent, EditorContext, useEditor} from "@tiptap/react";
import {StarterKit} from "@tiptap/starter-kit";
import {TextAlign} from "@tiptap/extension-text-align";
import {Underline} from "@tiptap/extension-underline";
import {Typography} from "@tiptap/extension-typography";
import RichTextToolbar from "../components/RichTextToolbar";
import RichTextLinkExtension from "../components/RichTextLinkExtension";
import {FormProvider, useForm} from "react-hook-form";
import FormField from "../../../components/react-hook-form/form-field";
import ReviseTextButton from "../../../components/revise-text/revise-text-button";

const BASENAME = process.env.PUBLIC_URL;

const statusList = [
    { value: 'pendente', label: 'Pendente' },
    { value: 'negado', label: 'Negado' },
    { value: 'backlog', label: 'Backlog' },
    { value: 'doing', label: 'Doing' },
    { value: 'concluido', label: 'Concluído' },
    { value: 'travado', label: 'Travado' },
];

const initialData = {
    date: new Date(),
    deadline: "",
    projects: [],
    status: "",
    devs: [],
    groups: [],
    title: "",
    description: "",
    gitlab: "",
    requester: "",
    responsible: "",
    attachs: [],
    exec_order: "",
    roadmap: false
}

const RequestForm = () => {
    const navigate = useNavigate();
    const {id} = useParams();
    const [state, setState] = useState(initialData);
    const [isLoading, setIsLoading] = useState(false);
    const [updateInfo, setUpdateInfo] = useState(null)
    const [attendance, setAttendance] = useState(null)
    const [route, setRoute] = useState(null)
    const [relatedRequest, setRelatedRequest] = useState([])
    const [related, setRelated] = useState("")
    const [requestsIds, setRequestsIds] = useState([])
    const [relatedIntegration, setRelatedIntegration] = useState([])
    const [integration, setIntegration] = useState('')
    const [integrationsIds, setIntegrationsIds] = useState([])

    const queryClient = useQueryClient();
    const attachesRef = useRef();
    const {user} = useContext(ProjectContext);
    const onlyMyRequests = user.permissions.includes("request_only_my_user");
    const canIntegrate = user.permissions.includes("integrations");
    const handleDateInput = (value, name) => setState(prev => ({...prev, [name]: value}));
    const handleSelect = (value, name) => setState(prev => ({...prev, [name]: value}));
    const handleInput = ({target: {name, value}}) => setState(prev => ({...prev, [name]: value}));

    const {projects, devs, groups, responsibles} = useFetchData();

    const methods = useForm({
        defaultValues: {
            textContent: ""
        }
    });

    const editor = useEditor({
        immediatelyRender: false,
        editorProps: {
            attributes: {
                autocomplete: "off",
                autocorrect: "off",
                autocapitalize: "off",
            },
        },
        extensions: [
            StarterKit,
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            Underline,
            Typography,
            RichTextLinkExtension
        ],
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            methods.setValue("textContent", html, {
                shouldValidate: true,
                shouldDirty: true,
            });
        }
    });

    const providerValue = useMemo(() => ({ editor }), [editor])

    useEffect(() => {
        document.title = "SISTEMA INTERNO";
    }, []);

    useEffect(() => {
        if (onlyMyRequests && !id) setState(prev => ({...prev, status: { value: 'pendente', label: 'Pendente' }}))
    }, [id, onlyMyRequests]);

    useQuery({
        queryFn: () => requestService.getAssociatedRequests(id).then(({data}) => {
            setRelatedRequest(data)
            return data
        }),
        queryKey: ['associatedRequest'],
        enabled: !!id
    })

    useQuery({
        queryFn: () => integrationsService.listRequests(id, 'request').then(({data}) => {
            setRelatedIntegration(data)
            return data
        }),
        queryKey: ['associatedIntegration'],
        enabled: !!canIntegrate && !!id,
    })

    useQuery({
        queryFn: () => requestService.getRequestsIds().then(({data}) => {
            setRequestsIds(data)
            return data
        }),
        queryKey: ['requestsIds']
    })

    useQuery({
        queryFn: () => integrationsService.getIntegrations().then(({data}) => {
            setIntegrationsIds(data)
            return data
        }),
        queryKey: ['integrationsIds'],
        enabled: !!canIntegrate && !!id,
    })

    const {refetch} = useQuery({
        queryFn: () => requestService.getById(id).then((data) => {
            const {created_at_formated, user_created, user_created_id, updated_at_formated, user_updated, attendanceSoftware, attendanceAfterSale} = data
            if (onlyMyRequests && (user_created_id !== user.id)) return navigate("/401")

            const updateInto = `Cadastrado em ${created_at_formated} por ${user_created} - Última atualização em ${updated_at_formated} por ${user_updated}`;

            const projects = data.projects.map(p => {
                return {label: p.name, value: p.id};
            });

            const devs = data.devs.map(d => {
                return {label: d.name, value: d.id};
            });

            const groups = data.groups.map(g => {
                return {label: g.name, value: g.id};
            });
            setState({
                date: data.date,
                deadline: data.deadline ?? "",
                status: statusList.find(s => s.value === data.status),
                title: data.title,
                gitlab: data.gitlab ?? "",
                requester: data.requester ?? "",
                responsible: data?.responsible ? {value: data.responsible.id, label: data.responsible.name} : "",
                attachs: data.attachs ?? "",
                roadmap: data.roadmap ?? false,
                devs,
                groups,
                projects,
                exec_order: data.exec_order ?? "",
                relatedRequest: relatedRequest ?? [],
                relatedIntegration: relatedIntegration ?? [],
            });

            setUpdateInfo(updateInto);
            if (attendanceSoftware) {
                setAttendance(attendanceSoftware.attendance_id);
                setRoute("software-attendances")
            }
            if (attendanceAfterSale) {
                setAttendance(attendanceAfterSale.after_sale_id);
                setRoute("after-sales/attendances")
            }

            editor.commands.setContent(data.description)
            methods.setValue("textContent", data.description, {
                shouldValidate: true,
                shouldDirty: true,
            });
            return data;
        }),
        queryKey: ['request', {id}],
        enabled: !!id,
    });
    const submit = () => {
        setIsLoading(true);

        const description = editor.getHTML();

        const newRq = prepareRelated(relatedRequest, "request");

        const newIntegration = prepareRelated(relatedIntegration, "integration");

        const formData = new FormData();

        const form = {
            ...state,
            date: state.date ? general.formatDate(state.date) : "",
            deadline: state.deadline ? general.formatDate(state.deadline) : "",
            projects: state.projects ? prepareValues(state.projects) : "",
            devs: state.devs ? prepareValues(state.devs) : "",
            groups: state.groups ? prepareValues(state.groups) : "",
            status: state.status?.value ?? "",
            responsible_id: state.responsible?.value ?? "",
            relatedRequest: newRq ? JSON.stringify(newRq) : "",
            relatedIntegration: newIntegration ? JSON.stringify(newIntegration) : "",
            description: description,
        }

        const attachesRow = [];
        attachesRef.current?.api.forEachNode((node) => {
            attachesRow.push(node.data);

            if (node.data?.file) formData.append("attaches[]", node.data.file);
        });

        Object.keys(form).forEach(key => {
            formData.append(key, form[key])
        });

        formData.append("attachs", JSON.stringify(attachesRow));

        if (!!id) formData.append('_method', 'PUT');

        const response = !!id ? requestService.edit(formData, id) : requestService.create(formData);

        response
            .then(({data}) => {
                toast.success("Salvo com sucesso.");
                id ? refetch() : navigate(`/requests/form/${data.id}`);
            })
            .catch((err) => {
                const {data: {message}} = err.response;
                toast.error(AlertMessage[message] ?? "Não foi possível salvar");
            })
            .finally(() => {
                queryClient.invalidateQueries('integrationsIds')
                queryClient.invalidateQueries('associatedRequest')
                queryClient.invalidateQueries('associatedIntegration')
                queryClient.invalidateQueries('requestsIds')
                setIsLoading(false)
            });
    }

    const prepareRelated = (value, type) => {

        if (value.length > 0) {
            return  value.map((r) => {
                const localId = String(r.id);

                if (type === "request"){
                    if (localId.includes("uuid-")) {
                        return {id: null, associated_request_id: r.associated_request_id}
                    } else {
                        return {id: r.id ,associated_request_id: r.associated_request_id}
                    }
                }
                if (type === "integration"){
                    if (localId.includes("uuid-")) {
                        return {id: null, associated_integration_id: r.associated_integration_id, request_id: r.request_id}
                    } else {
                        return {id: r.id, associated_integration_id: r.associated_integration_id, request_id: r.request_id}
                    }
                }

                return null;
            });
        }
        return null;
    };

    const prepareValues = (value) => {
        if (value.length > 0) {
            return JSON.stringify(value.map(m => m.value))
        }

        return null;
    }

    const deleteRequest = () => {
        MySwal.fire({
            icon: "warning",
            title: "Excluir!",
            showCancelButton: true,
            text: "Tem certeza que deseja excluir solicitação?",
            confirmButtonText: "Sim",
            cancelButtonText: "Não",
            confirmButtonColor: '#e64942',
            reverseButtons: true
        }).then(({isConfirmed}) => {
            if (isConfirmed) {
                setIsLoading(true);

                requestService.delete(id)
                    .then(() => {
                        navigate(`/requests`);
                    })
                    .catch((err) => {
                        const {data: {message}} = err.response;
                        toast.error(AlertMessage[message] ?? "Não foi possível excluir");
                    })
                    .finally(() => setIsLoading(false));
            }
        });
    }

    const deleteRelatedRequest = (relatedRequest_id) => {
        const localId = String(relatedRequest_id);
        if (localId.includes("uuid-")){
            setRelatedRequest((prev) => prev.filter((item) =>  item.id !== relatedRequest_id));
        }else{
            requestService.deleteAssociatedRequest(relatedRequest_id)
                .catch((err) => {
                    const {data: {message}} = err.response;
                    toast.error(AlertMessage[message] ?? "Não foi possível excluir");
                }).then(setRelatedRequest((prev) => prev.filter((item) =>  item.id !== relatedRequest_id)))
        }
    }

    const deleteIntegration = (integration_id) => {
        const localId = String(integration_id);
        if (localId.includes("uuid-")){
            setRelatedIntegration((prev) => prev.filter((item) =>  item.id !== integration_id));
        }else{
            IntegrationsService.deleteAssociatedIntegration(integration_id)
                .catch((err) => {
                    const {data: {message}} = err.response;
                    toast.error(AlertMessage[message] ?? "Não foi possível excluir");
                }).then(setRelatedIntegration((prev) => prev.filter((item) =>  item.id !== integration_id)))
        }
    }

    const addRelatedRequest = useCallback(() => {
        if (!related) {
            toast.error(AlertMessage.generalEmpty);
            return
        }

        let duplicate = false
        let notExists = false
        relatedRequest.forEach(r => {
            if(String(r.associated_request_id) === String(related) || String(r.request_id) === String(related)) duplicate = true;
        })

        if(!requestsIds.some(item => String(item.id) === String(related)))  notExists = true;

        if(!duplicate && !notExists) {
            setRelatedRequest(prev => ([...prev, {
                id: "uuid-" + related,
                request_id: id,
                associated_request_id: related
            }]))
            setRelated("")
        }else{
            if (duplicate) toast.error(AlertMessage.duplicateRelation)
            if (notExists) toast.error(AlertMessage.notExistRequest)
        }
    }, [id, related, relatedRequest, requestsIds, setRelatedRequest]);

    const addRelatedIntegration = useCallback(() => {
        if (!integration){
            toast.error(AlertMessage.generalEmpty);
            return
        }

        let duplicate = false
        relatedIntegration.forEach(r => {
            if(String(r.associated_integration_id) === String(integration) || String(r.request_id) === String(integration)) duplicate = true;
        })

        let notExists = false
        if(!integrationsIds.some(item => String(item.id) === String(integration)))  notExists = true;

        if (!duplicate && !notExists){
            setRelatedIntegration(prev => ([...prev, {
                id: "uuid-" + integration,
                request_id: id,
                associated_integration_id: integration
            }]))
            setIntegration("")
        }else{
            if (duplicate) toast.error(AlertMessage.duplicateRelation)
            if (notExists) toast.error(AlertMessage.notExistIntegration)
        }

    }, [id, integration, integrationsIds, relatedIntegration, setRelatedIntegration]);

    const openRequest = (requestId) => {
        window.open(`${BASENAME}/requests/form/${requestId}`);
    }

    const getRelatedRequestId = (relatedItem) => {
        if (relatedItem.request_id === undefined || relatedItem.request_id === null) {
            return relatedItem.associated_request_id;
        }

        return String(relatedItem.request_id) === String(id)
            ? relatedItem.associated_request_id
            : relatedItem.request_id;
    }

    const openIntegration = (requestId) => {
        window.open(`${BASENAME}/integrations/form/${requestId}`);
    }
    const openAttendance = (attendanceId) => {
        window.open(`${BASENAME}/${route}/form/${attendanceId}`);
    }

    return (
        <TemplateDefault title="Nova solicitação" isLoading={isLoading}>
            <Card title={updateInfo} headerClass="text-center" titleClass={"text-sm"} wrapperClass={"relative"}>
                {
                    attendance &&
                    <div onClick={() => openAttendance(attendance)}
                         className={"flex items-center text-lg text-blue-700 cursor-pointer hover:underline absolute top-[5px]"}>
                        Atendimento: #{attendance} <FaExternalLinkAlt className={"ml-2"}/>
                    </div>

                }

                <div className="grid lg:grid-cols-12 gap-4">
                    <div className="col-span-12 lg:col-span-2">
                        <InputDateTime
                            id={"date"} name={"date"} label={"Solicitação"}
                            selected={state.date}
                            onChange={(value) => handleDateInput(value, "date")}
                        />
                    </div>
                    <div className="col-span-12 lg:col-span-2">
                        <InputDateTime
                            id={"deadline"} name={"deadline"} label={"Prazo de entrega"}
                            selected={state.deadline}
                            onChange={(value) => handleDateInput(value, "deadline")}
                        />
                    </div>
                    <InputForm id={"title"} name="title" value={state.title} onChange={handleInput} label="Título"
                               col="col-span-12 lg:col-span-8"/>

                    <div className="col-span-12 lg:col-span-3 text-gray-900">
                        <SelectComponent
                            isMulti
                            instanceId="groups"
                            label="Grupos"
                            options={groups ?? []}
                            value={state.groups}
                            onChange={(value) => handleSelect(value, "groups")}
                            name="groups"
                        />
                    </div>

                    <div className="col-span-12 lg:col-span-3 text-gray-900">
                        <SelectComponent
                            isMulti
                            instanceId="projects"
                            label="Projetos"
                            options={projects ?? []}
                            value={state.projects}
                            onChange={(value) => handleSelect(value, "projects")}
                            name="projects"
                        />
                    </div>

                    <InputForm id={"requester"} name="requester" value={state.requester} onChange={handleInput}
                               label="Solicitante "
                               col="col-span-12 lg:col-span-3"/>

                    <div className="col-span-12 lg:col-span-3 text-gray-900">
                        <SelectComponent
                            instanceId="responsible"
                            label="Atendente"
                            options={responsibles ?? []}
                            value={state.responsible}
                            onChange={(value) => handleSelect(value, "responsible")}
                            name="responsible"
                        />
                    </div>

                    <div className={"col-span-12 grid lg:grid-cols-12 gap-4"}>

                        <div className="col-span-12 lg:col-span-2 text-gray-900">
                            <SelectComponent
                                instanceId="status"
                                label="Status"
                                options={statusList}
                                value={state.status}
                                isDisabled={onlyMyRequests}
                                onChange={(value) => handleSelect(value, "status")}
                            />
                        </div>

                        <div className="col-span-12 lg:col-span-2 text-gray-900">
                            <SelectComponent
                                isMulti
                                instanceId="devs"
                                label="Devs"
                                options={devs ?? []}
                                value={state.devs}
                                onChange={(value) => handleSelect(value, "devs")}
                                name="devs"
                            />
                        </div>

                        <InputForm id={"gitlab"} name="gitlab" value={state.gitlab}
                                   onChange={handleInput} label="GitLab"
                                   col="col-span-12 lg:col-span-4"/>

                        <InputForm type={"number"} id={"exec_order"} name="exec_order" value={state.exec_order}
                                   onChange={handleInput} label="Ordem"
                                   col="col-span-12 lg:col-span-2"/>

                        <InputCheckbox id={"roadmap"} name={"roadmap"} label={"Roadmap"} col="col-span-12 lg:col-span-1"
                                       onChange={({target: {checked}}) => setState(prev => ({
                                           ...prev,
                                           roadmap: checked
                                       }))}
                                       checked={state.roadmap}
                        />

                        <InputForm type={"number"} id="item_description" label="Solicitação relacionada" value={related}
                                   col="col-span-12 lg:col-span-2"
                                   onChange={({target: {value}}) => setRelated(value)}
                        />
                        <Button onClick={addRelatedRequest} target="primary" col="col-span-12 lg:col-span-1">
                            <FaPlus/>
                        </Button>

                        {
                            relatedRequest &&
                            <div className={relatedRequest.length >= 6 ? "col-span-9 flex items-center" : "col-span-3 flex items-center"}>
                                {relatedRequest.map((r) => (
                                    <div
                                        key={r.id}
                                        className="flex items-center"
                                    >
                                        <p
                                            className={"text-lg hover:text-blue-700 hover:underline cursor-pointer mr-1"}
                                            onClick={() => openRequest(getRelatedRequestId(r))}
                                        >
                                            #{getRelatedRequestId(r)}
                                        </p>
                                        <button
                                            className="text-red-500 hover:text-red-700 cursor-pointer mr-2"
                                            onClick={() => deleteRelatedRequest(r.id)}
                                        >
                                            X
                                        </button>
                                    </div>
                                ))}
                            </div>
                        }

                        {
                            canIntegrate &&
                                <>
                                    <InputForm
                                        type={"number"}
                                        id="related_integration"
                                        label="Integração relacionada"
                                        value={integration}
                                        col="col-span-12 lg:col-span-2"
                                        onChange={({ target: { value } }) => setIntegration(value)}
                                    />
                                    <Button onClick={addRelatedIntegration} target="primary" col="col-span-12 lg:col-span-1">
                                        <FaPlus />
                                    </Button>
                                    {relatedIntegration && (
                                        <div className={relatedIntegration.length >= 6 ? "col-span-9 flex items-center" : "col-span-3 flex items-center"}>
                                            {relatedIntegration.map((r) => (
                                                <div key={r.associated_integration_id} className="flex items-center">
                                                    <p
                                                        className={"text-lg hover:text-blue-700 hover:underline cursor-pointer mr-1"}
                                                        onClick={() => openIntegration(r.associated_integration_id)}
                                                    >
                                                        #{r.associated_integration_id}
                                                    </p>
                                                    <button
                                                        className="text-red-500 hover:text-red-700 cursor-pointer mr-2"
                                                        onClick={() => deleteIntegration(r.id)}
                                                    >
                                                        X
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                        }
                    </div>

                    <FormProvider {...methods}>
                        <div className={"col-span-12"}>
                            <EditorContext.Provider value={ providerValue }>
                                <div className="text-editor">
                                    <RichTextToolbar/>
                                    <div className="content-wrapper">
                                        <EditorContent editor={editor} className="editor-content"/>
                                    </div>
                                </div>
                            </EditorContext.Provider>

                            <div className={"mt-4"}>
                                <FormField
                                    name={"textContent"}
                                    customRender={field => (
                                        <ReviseTextButton
                                            value={field.value}
                                            setValue={(value) => field.onChange(value ?? null)}
                                            setIsLoading={setIsLoading}
                                            editor={editor}
                                            module={"requests"}
                                            url={"/api/si/requests/text-correction"}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </FormProvider>
                </div>

                <div className={"col-span-12 lg:col-span-12"}>
                    <AttachesTable attaches={state.attachs} gridRef={attachesRef} submit={submit}/>
                </div>
            </Card>

            <Card wrapperClass="mt-3">
                <div className="flex justify-end gap-3">
                    {
                        id &&
                        <Button target="danger" onClick={deleteRequest}>
                            Excluir
                        </Button>
                    }
                    <Button target="warning" onClick={() => navigate("/requests")}>
                        Cancelar
                    </Button>
                    <Button target="success" onClick={() => submit()}>
                        Salvar
                    </Button>
                </div>
            </Card>
        </TemplateDefault>
    );
};

export default RequestForm;
